import { zodResolver } from "@hookform/resolvers/zod";
import { Button, GeneralDialog, InputWithLabel } from "@ptsd/uikit";
import i18next from "i18next";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Dialog, Form, View } from "tamagui";
import { z } from "zod";

import { api } from "~/utils/api";

const formSchema = z.object({
  name: z
    .string()

    .min(2, {
      message: i18next.t("groups:validation-errors.min-length"),
    })
    .max(50, {
      message: i18next.t("groups:validation-errors.max-length"),
    }),
});

type RenameNewGroupForm = z.infer<typeof formSchema>;

export const RenameGroupDialog: React.FC<{
  groupId: string;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ groupId, open, setOpen }) => {
  const { t } = useTranslation("groups");

  const { control, reset, handleSubmit } = useForm<RenameNewGroupForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
    },
  });

  const utils = api.useUtils();

  const { mutate, isLoading } = api.group.rename.useMutation({
    onSuccess: () => {
      void utils.group.list.invalidate();
      setOpen(false);
    },
  });

  const onSubmit = (values: RenameNewGroupForm) => {
    mutate({ name: values.name, groupId });
    reset();
  };

  return (
    <Dialog data-id="rename-group-dialog" open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay
          zIndex={0}
          key={"add-group-dialog-overlay"}
          style={{
            backgroundColor: "#6A696966",
            backdropFilter: "blur(10px)",
          }}
        />
        <GeneralDialog.Content
          data-id="rename-group-dialog-content"
          maxWidth={600}
          width="100%"
        >
          <Form onSubmit={handleSubmit(onSubmit)}>
            <View
              width="100%"
              gap={"$5"}
              alignItems="center"
              paddingTop={"$5"}
              paddingBottom={"$4"}
            >
              <GeneralDialog.Title data-id="rename-group-dialog-title">
                {t("rename-group.title")}
              </GeneralDialog.Title>

              <Controller
                control={control}
                name="name"
                render={({ field, fieldState }) => (
                  <InputWithLabel
                    data-id="rename-group-input"
                    maxWidth={400}
                    variant="inline"
                    size="md"
                    rounded="md"
                    label={t("rename-group.label")}
                    placeholder={t("rename-group.placeholder")}
                    isError={!!fieldState.error?.message}
                    error={fieldState.error?.message}
                    onChangeText={field.onChange}
                    {...field}
                  />
                )}
              />
            </View>

            <GeneralDialog.ControllArea
              gap={"$2"}
              alignItems="center"
              paddingVertical={"$3"}
            >
              <Form.Trigger asChild>
                <Button
                  data-id="rename-group-submit-button"
                  variant="primary"
                  minWidth={180}
                  maxWidth={320}
                  width={"100%"}
                  size="md"
                  rounded="lg"
                >
                  {isLoading
                    ? t("rename-group.submit.loading")
                    : t("rename-group.submit.text")}
                </Button>
              </Form.Trigger>

              <GeneralDialog.Close data-id="rename-group-close-button">
                {t("rename-group.close")}
              </GeneralDialog.Close>
            </GeneralDialog.ControllArea>
          </Form>
        </GeneralDialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};
